"use client";
import React, { useEffect, useRef } from 'react';
import Container from "../container";
import styles from './styles.module.scss';

type StickyPosition = 'left' | 'right';

interface DropDownProps {
  open: boolean;
  stickyPosition?: StickyPosition;
  className?: string;
  childrenClassName?: string;
  children: React.ReactNode;
  onClose?: () => void; // optional outside-click close
}

export const DropDown: React.FC<DropDownProps> = ({
  open,
  className = '',
  stickyPosition = 'left',
  children,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!onClose) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div ref={ref} className={styles.root}>
      <Container
        rootClassName={`${styles.rootContainer} ${stickyPosition == 'left' ? styles.containerLeft : styles.containerRight} ${className}`}
        className={styles.container}
        blur={true}
      >
        {children}
      </Container>
    </div>
  );
};

interface DropDownItemProps {
  onSelect?: () => void;
  className?: string;
  children: React.ReactNode;
}

export const DropDownItem: React.FC<DropDownItemProps> = ({ onSelect, className = '', children }) => {
  return (
    <div
      onClick={onSelect}
      className={`${styles.item} ${className}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelect?.();
          e.preventDefault();
        }
      }}
    >
      {children}
    </div>
  );
};

export default DropDown;
